import { useState, useEffect, useRef, useMemo } from 'react';

import moment from "moment";
import dayjs from "dayjs";
import { useColors } from '../../Context/ColorsContext';

import { DashboardReportApi } from '../../services/DashboardReportApi';
import { DashboardLicensesApi } from '../../services/DashboardLicensesApi';
import { MenuItem, FormControl, TableBody, TableContainer, TableRow, Paper, Grid } from '@mui/material';

import { useDownloadExcel } from '../../Hooks/useDownloadExcel';

// Components
import { ComponentNoDataCharts } from '../../components/NoDataGraphic';
import { FiltersLicenses } from '../../components/FiltersLicenses';
import { BackdropLoading } from '../../components/BackdropLoading';
import { CardGraphic } from '../../components/CardGraphic';
import { BoxModal, Cell, CustomButtonV2, CustomSelectDemo, CustomTable, CustomTableHead, PageTitle } from '../styles';

// MUI Components
import { Modal } from '@mui/material';

// Styles
import { Container } from '../styles';
import {
  ContainerGraphics,
  ContentGraphics,
  CustomTitleGraphics,
} from './style';

import { useTranslation } from 'react-i18next';
import { GraphicTrendActiveUsers } from '../../components/atoms/modalCards/GraphicTrendActiveUsers';


export const LicensesReport = (props) => {

  const { t } = useTranslation();
  const { colors } = useColors();
  const dashboardApi = new DashboardReportApi();
  const dashboardLicensesApi = new DashboardLicensesApi();

  const chartRef = useRef(null);

  const [usersPerDay, setUsersPerDay] = useState([]);
  const [usersByRole, setUsersByRole] = useState([]);
  const [usersLastSession, setUsersLastSession] = useState([]);
  const [usersLicenses, setUsersLicenses] = useState([]);

  const [open, setOpen] = useState(false);
  const [age, setAge] = useState('');
  const [showDates, setShowDates] = useState(false);
  const [openModalTrendUsers, setOpenModalTrendUsers] = useState(false);
  const [dataBarGraphics, setDataBarGraphics] = useState([]);
  const [allSeriesColors, setAllSeriesColors] = useState([]);
  const [searchVicePresidText, setSearchVicePresidText] = useState('');
  const [searchCoreText, setSearchCoreText] = useState('');
  const [searchSiteText, setSearchSiteText] = useState('');
  const [searchCompanyText, setSearchCompanyText] = useState('');
  const [searchRolesText, setSearchRolesText] = useState('');
  // select presidency
  const [filterPresidency, setFilterPresidency] = useState([]);
  // select core
  const [filterCore, setFilterCore] = useState([]);
  // select sites
  const [filterSites, setFilterSites] = useState([]);
  const [filterCompany, setFilterCompany] = useState([]);
  const [filterRoles, setFilterRoles] = useState([]);
  const [finalDate, setFinalDate] = useState(moment(new Date()).format("YYYY-MM-DD"));
  const [initialDate, setInitialDate] = useState(moment(finalDate).subtract(1, "months").format("YYYY-MM-DD"));
  const [errorsDate, setErrorsDate] = useState({ startDate: false, endDate: false });
  const [filters, setFilters] = useState({
    vicePresidency: [],
    core: [],
    site: [],
    company: [],
    roles: [],
    endDate: null,
    startDate: null,
    relativeDate: 0,
  });
  const fieldsConfig = {
    [t("dashboard.licenses.role")]: 'label',
    [t("dashboard.licenses.quantity")]: 'value'
  };
  const { handleDownloadExcelList } = useDownloadExcel(usersByRole, fieldsConfig, t("dashboard.licenses.activeUsers"));

  const colorsGraphics = [
    '#10476b', '#0778a6', '#b6b310', '#145f89', '#105b73', '#0b4b7a', '#0d5b8c',
    '#125f83', '#095f76', '#066d8a', '#1c6b96', '#1173a0', '#138fb4', '#11a0a0',
    '#0c8a9d', '#0d7b88', '#92a611', '#a9b211', '#838e13', '#6b7b14'
  ];

  const containsText = (text, search) => text?.toLowerCase().indexOf(search.toLowerCase()) > -1;

  // Options Select Sites
  const optionsSites = useMemo(() => filterSites?.filter((option) => containsText(option.name, searchSiteText)) || [],
    [filterSites, searchSiteText]);
  // Options Select Presidency
  const optionsPresidency = useMemo(() => filterPresidency?.filter((option) => containsText(option.name, searchVicePresidText)) || [],
    [filterPresidency, searchVicePresidText]);
  // Options Select Troncal
  const optionsCore = useMemo(() => filterCore?.filter((option) => containsText(option.name, searchCoreText)) || [],
    [filterCore, searchCoreText]);
  // Options Select Company
  const optionsCompany = useMemo(() => filterCompany?.filter((option) => containsText(option.name, searchCompanyText)) || [],
    [filterCompany, searchCompanyText]);
  // Options Select Contract
  const optionsRoles = useMemo(() => filterRoles?.filter((option) => containsText(option.name, searchRolesText)) || [],
    [filterRoles, searchRolesText]);

  const areAllItemsSelected = (filters, options, key, category = null) => {
    const filteredOptions = category ? options.filter(item => item.category.name === category) : options;
    return filters[key].length > 0 && filteredOptions.length > 0 && filters[key].length === filteredOptions.length;
  };

  const isAllSelected = areAllItemsSelected(filters, optionsSites, 'site');
  const isAllSystemsSelected = areAllItemsSelected(filters, optionsSites, 'site', 'Sistema');
  const isAllStationsSelected = areAllItemsSelected(filters, optionsSites, 'site', 'Estación');
  const isAllCoresSelected = areAllItemsSelected(filters, optionsCore, 'core');

  const isAllPresidencySelected = filters.vicePresidency.length === optionsPresidency.length;
  const isAllCompanySelected = filters.company.length === optionsCompany.length;
  const isAllRolesSelected = filters.roles.length === optionsRoles.length;

  useEffect(() => {
    getPlantManagementReport();
    getPlantManagementCompany();
    getPlantManagementRoles();

    postUsersPerDay();
    postUsersByRole();
    postUsersLastSession();
    postUsersLicenses();
  }, []);

  const getPlantManagementReport = async () => {
    const response = await dashboardApi.getPlantManagementReport();
    if (response.status === 200) {
      setFilterPresidency(response.data);
      const filterItem = response.data?.find(el => el.id === 1) || [];
      setFilterCore(filterItem.plantTrunks);
    }
  }

  const getPlantManagementCompany = async () => {
    const response = await dashboardApi.getPlantManagementCompany();
    if (response.status === 200) {
      setFilterCompany([...response.data, { id: 0, name: t("dashboard.licenses.noCompany") }]);
    }
  }

  const getPlantManagementRoles = async () => {
    const response = await dashboardApi.getRolesManagementReport();
    if (response.status === 200) {
      setFilterRoles(response.data.data);
    }
  }

  const postUsersPerDay = async (params = {}) => {
    const response = await dashboardLicensesApi.usersPerDay(params);
    if (response.status === 200) {
      setUsersPerDay(response.data);
    }
  }

  const postUsersByRole = async (params = {}) => {
    const response = await dashboardLicensesApi.UsersByRole(params);
    if (response.status === 200) {
      setUsersByRole(response.data);
    }
  }

  const postUsersLastSession = async (params = {}) => {
    const response = await dashboardLicensesApi.UsersLastSession(params);
    if (response.status === 200) {
      setUsersLastSession(response.data);
    }
  }

  const postUsersLicenses = async (params = {}) => {
    const response = await dashboardLicensesApi.UsersLicenses(params);
    if (response.status === 200) {
      setUsersLicenses(response.data);
    }
  }

  const handleSendFilters = async (e) => {
    e.preventDefault();
    const { startDate, endDate, core, site, company, roles, relativeDate } = filters;
    setOpen(true);
    const prepareFilters = () => ({
      managementIds: [1],
      trunkIds: core?.map(el => el.id),
      plantIds: site?.map(el => el.id),
      companyIds: company?.map(el => el.id),
      roleIds: roles?.map(el => el.id),
      validityFrom: startDate,
      validityTo: endDate,
      relativeDate: relativeDate,
    });


    try {
      await postUsersPerDay(prepareFilters());
      await postUsersByRole(prepareFilters());
      await postUsersLastSession(prepareFilters());
      await postUsersLicenses(prepareFilters());

    } catch (err) {
      console.error('Error fetching data', err);
    } finally {
      setOpen(false);
    }
  }



  const handleVicePresidencyChange = (value, optionsPresidency) => {
    const isAllSelected = value[value.length - 1] === 'allPresidency';
    let allPlantTrunks = [];

    if (isAllSelected) {
      allPlantTrunks = isAllPresidencySelected ? [] : optionsPresidency.flatMap(el => el.plantTrunks);
    } else {
      allPlantTrunks = value.flatMap(item => item.plantTrunks || []);
    }

    setFilterCore(allPlantTrunks);

    const selectedTrunksIds = allPlantTrunks.map(trunk => trunk.id);
    const filteredPlants = filterSites.filter(plant => selectedTrunksIds.includes(plant.plantTrunk.id));

    setFilterSites(filteredPlants);

    // Limpiar filtros troncales y plantas en caso de que se desmarquen vicepresidencias
    const updatedCore = filters.core?.filter(core => allPlantTrunks.some(trunk => trunk.id === core.id));
    const updatedSites = filters.site?.filter(site => filteredPlants.some(plant => plant.id === site.id));

    return {
      vicePresidency: isAllSelected ? (isAllPresidencySelected ? [] : optionsPresidency) : value,
      core: updatedCore,
      site: updatedSites,
    };
  };

  const handleCoreChange = (value, optionsCore) => {
    const isAllSelected = value[value.length - 1] === 'AllCore';
    const allPlants = filterPresidency.flatMap(item => item.plants);
    let arrPlantsSelected = [];

    if (isAllSelected) {
      arrPlantsSelected = isAllCoresSelected ? [] : allPlants.filter(plant =>
        optionsCore?.some(troncal => troncal.id === plant.plantTrunk.id)
      )
    } else {
      arrPlantsSelected = allPlants.filter(plant =>
        value?.some(troncal => troncal.id === plant.plantTrunk.id)
      )
    }

    setFilterSites(arrPlantsSelected);

    // limpiar filtro plantas en caso de que se desmarquen las opciones en troncales
    const updatedSites = filters.site?.filter(site =>
      arrPlantsSelected.some(el => el.plantTrunk.id === site.plantTrunk.id)
    )

    return {
      core: isAllSelected ? (isAllCoresSelected ? [] : optionsCore) : value,
      site: updatedSites,
    }
  };

  const handleSiteChange = (value, optionsSites) => {
    if (value[value.length - 1] === 'selectAll') {
      return { site: isAllSelected ? [] : optionsSites };
    } else if (value[value.length - 1] === 'AllSystems') {
      const systemSites = filterSites.filter(site => site.category.name === 'Sistema');
      return { site: isAllSystemsSelected ? [] : systemSites };
    } else if (value[value.length - 1] === 'AllStations') {
      const stationSites = filterSites.filter(station => station.category.name === 'Estación');
      return { site: isAllStationsSelected ? [] : stationSites };
    }
    return { site: value };
  };

  const handleDateChange = (date) => {
    const formattedDate = moment(new Date(date));
    return formattedDate.isValid() ? formattedDate.format("YYYY-MM-DD") : null;
  };

  const handleCompanyChange = (value, optionsCompany) => {
    const isAllSelected = value[value.length - 1] === 'allCompany';

    return {
      company: isAllSelected ? (isAllCompanySelected ? [] : optionsCompany) : value,
    }
  }

  const handleRoleChange = (value, optionsRole) => {
    if (value[value.length - 1] === 'allRoles') {
      return { roles: isAllRolesSelected ? [] : optionsRole };
    }
    return { roles: value };
  }

  const handleChangeFilters = (e, name, type) => {
    let value;

    if (type === "checkbox") {
      value = e.target.checked;
      handleSwitchCompanies(value);
      return
    } else if (type === "date") {
      value = handleDateChange(e);

      if (name === "endDate" && filters.startDate && dayjs(value).isBefore(filters.startDate)) {
        // Si la fecha "hasta" es menor que la fecha "desde", limpiar el valor de "endDate"
        setFilters(prevFilters => ({ ...prevFilters, endDate: null }));
        setErrorsDate(prev => ({ ...prev, endDate: true }));
      } else if (name === "startDate" && filters.endDate && dayjs(filters.endDate).isBefore(value)) {
        // Si la fecha "hasta" existente es menor que la nueva "startDate", limpiar "endDate"
        setFilters(prevFilters => ({ ...prevFilters, startDate: value, endDate: null }));
        setErrorsDate(prev => ({ ...prev, endDate: true }));
      } else {
        // Si la validación pasa, actualizar el filtro y limpiar el error
        setFilters(prevFilters => ({ ...prevFilters, [name]: value }));
        setErrorsDate(prev => ({ ...prev, [name]: false }));
      }
    } else {
      value = e?.target?.value;

      let newFilters;
      switch (name) {
        case 'site':
          newFilters = handleSiteChange(value, optionsSites);
          break;
        case 'vicePresidency':
          newFilters = handleVicePresidencyChange(value, optionsPresidency);
          break;
        case 'core':
          newFilters = handleCoreChange(value, optionsCore);
          break;
        case 'company':
          newFilters = handleCompanyChange(value, optionsCompany);
          break;
        case 'roles':
          newFilters = handleRoleChange(value, optionsRoles);
          break;
        default:
          newFilters = { [name]: value };
      }

      setFilters(prevFilters => ({ ...prevFilters, ...newFilters }));
      return;
    }

    setFilters(prevFilters => ({ ...prevFilters, [name]: value }));
  };

  const handleFiltersModal = (e) => {
    const selectedValue = e.target.value;
    setAge(selectedValue);
    setShowDates(selectedValue === 40);
  }

  // Clear all filters
  const handleClearFilters = () => {
    let clean = {
      vicePresidency: [],
      core: [],
      site: [],
      company: [],
      roles: [],
      startDate: null,
      endDate: null,
      relativeDate: 0,
    }
    setErrorsDate({ startDate: false, endDate: false });
    setFilterSites([]);
    setFinalDate('');
    setInitialDate('');
    setFilters(clean);
  }


  let options = {
    chart: {
      height: 350,
      type: 'bar',
      toolbar: { show: false }
    },
    colors: colorsGraphics,
    plotOptions: {
      bar: {
        columnWidth: '45%',
        distributed: true,
      }
    },
    dataLabels: {
      enabled: false
    },
    legend: {
      show: false
    },
    xaxis: {
      labels: {
        style: {
          colors: colorsGraphics,
          fontSize: '12px'
        }
      }
    },
    tooltip: {
      y: {
        formatter: val => parseInt(val)
      }
    }
  };

  const multiAxisOptions = {
    chart: {
      type: 'line',
      toolbar: { show: false }
    },
    xaxis: {
      type: 'datetime',
      labels: {
        rotate: -45,
        // formatter: val => moment(val).format('DD MMM'),
        format: 'dd MMM',
        datetimeUTC: false
      },
      tickAmount: 10
    },
    colors: colorsGraphics.slice(0, 3),
    legend: { show: false },
    tooltip: {
      y: {
        formatter: val => parseInt(val)
      }
    }
  };

  // options Donut
  const donutOptions = {
    chart: { type: 'donut' },
    colors: ["#10476b", "#0778a6", "#b6b310", "#145f89", "#105b73"],
    legend: { position: 'bottom' },
    dataLabels: {
      formatter: function (val, opts) {
        return opts.w.config.series[opts.seriesIndex];
      }
    }
  };

  const handleChangeHistorical = async (value, name) => {
    setFilters(prevFilters => {
      const updatedFilters = { ...prevFilters, [name]: value };
      fetchHistoricalData(updatedFilters);
      return updatedFilters;
    });
  };

  const fetchHistoricalData = async (filters) => {
    setOpen(true);

    const { startDate, endDate, core, site, company, roles, relativeDate } = filters;

    const preparedFilters = {
      managementIds: [1],
      trunkIds: core?.map(el => el.id),
      plantIds: site?.map(el => el.id),
      companyIds: company?.map(el => el.id),
      roleIds: roles?.map(el => el.id),
      validityFrom: startDate,
      validityTo: endDate,
      relativeDate: relativeDate,
    };

    try {
      postUsersPerDay(preparedFilters)
    } catch (error) {
      console.error("Error fetching historical data", error);
    } finally {
      setOpen(false);
    }
  };



  return (
    <Container open={props.open} style={{ marginBottom: 20 }}>
      <Grid container spacing={2}>

        <Grid item xs={12} sm={3} lg={2} xl={3}>
          <PageTitle>{t('dashboard.titleLicenses')}</PageTitle>
        </Grid>

        <Grid item xs={12} sm={9} lg={10} xl={9} mb={2}>
          <FiltersLicenses
            filters={filters}
            filterCore={filterCore}
            filterSites={filterSites}
            filterRoles={filterRoles}
            optionsPresidency={optionsPresidency}
            optionsCore={optionsCore}
            optionsSites={optionsSites}
            optionsCompany={optionsCompany}
            optionsRoles={optionsRoles}
            isAllSelected={isAllSelected}
            isAllSystemsSelected={isAllSystemsSelected}
            isAllStationsSelected={isAllStationsSelected}
            isAllCoresSelected={isAllCoresSelected}
            isAllPresidencySelected={isAllPresidencySelected}
            isAllCompanySelected={isAllCompanySelected}
            isAllRolesSelected={isAllRolesSelected}

            errorsDate={errorsDate}
            handleSendFilters={handleSendFilters}
            handleClearFilters={handleClearFilters}
            handleChangeFilters={handleChangeFilters}
            setSearchVicePresidText={setSearchVicePresidText}
            setSearchCoreText={setSearchCoreText}
            setSearchSiteText={setSearchSiteText}
            setSearchCompanyText={setSearchCompanyText}
            setSearchRolesText={setSearchRolesText}
          />
        </Grid>
      </Grid>


      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ContainerGraphics>
            <CardGraphic
              type="bar"
              options={{
                ...options,
                xaxis: {
                  ...options.xaxis,
                  categories: usersLicenses.categories || [],
                }
              }}
              series={[{ data: usersLicenses.data || [] }]}
              chartRef={chartRef}
            />
          </ContainerGraphics>
        </Grid>

        {/* Último Inicio de Sesión */}
        <Grid item xs={12} md={6} lg={4}>
          <ContentGraphics width='100%' height='100%' id='1'>
            <CustomTitleGraphics>{t("dashboard.licenses.lastLogin")}</CustomTitleGraphics>
            {usersLastSession.series?.some(el => el > 0) ? (() => {
              return (
                <CardGraphic
                  type="donut"
                  options={{
                    ...donutOptions,
                    labels: usersLastSession.labels || [],
                  }}
                  series={usersLastSession.series || []}
                  chartRef={chartRef}
                />
              )
            })() : (
              <ComponentNoDataCharts isFlow={false} height={'100%'} />
            )}
          </ContentGraphics>
        </Grid>

        {/* Usuarios Activos */}
        <Grid item xs={12} md={6} lg={4}>
          <ContentGraphics width='100%' height='100%' id='2'>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <CustomTitleGraphics>{t("dashboard.licenses.activeUsers")}</CustomTitleGraphics>
              <FormControl>
                <CustomSelectDemo
                  value={filters.relativeDate}
                  onChange={(e) => handleChangeHistorical(e.target.value, 'relativeDate')}
                  displayEmpty
                  sx={{ minWidth: '150px' }}
                >
                  <MenuItem value={0}>{t("dashboard.licenses.modal.lastMonth")}</MenuItem>
                  <MenuItem value={1}>{t("dashboard.licenses.modal.last6Month")}</MenuItem>
                  <MenuItem value={2}>{t("dashboard.licenses.lastYear")}</MenuItem>
                </CustomSelectDemo>
              </FormControl>
            </div>
            {usersPerDay.series?.[0]?.data?.some(val => val > 0) ? (() => {
              return (
                <CardGraphic
                  type="line"
                  options={multiAxisOptions}
                  series={[
                    {
                      name: t("dashboard.licenses.users"),
                      data: usersPerDay.labels?.map((label, i) => ({
                        x: label,
                        y: usersPerDay.series[0].data[i] || 0
                      }))
                    }
                  ]}
                  chartRef={chartRef}
                />
              )
            })() : (
              <ComponentNoDataCharts isFlow={false} height={'100%'} />
            )}
          </ContentGraphics>
        </Grid>

        {/* Usuarios por Rol */}
        <Grid item xs={12} md={152} lg={4}>
          <ContentGraphics width='100%' height='100%' id='3'>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: "center", justifyContent: 'space-between' }}>
              <CustomTitleGraphics>{t("dashboard.licenses.usersByRole")}</CustomTitleGraphics>
              <CustomButtonV2
                sx={{ width: '120px' }}
                btnType="primary"
                onClick={handleDownloadExcelList}>
                {t("dashboard.download")}
              </CustomButtonV2>
            </div>
            <TableContainer component={Paper} style={{ marginTop: "10px", height: '300px', borderRadius: '8px' }}>
              <CustomTable stickyHeader aria-label="sticky table" size="small">
                <CustomTableHead>
                  <TableRow>
                    <Cell><b>{t("dashboard.licenses.role")}</b></Cell>
                    <Cell><b>{t("dashboard.licenses.quantity")}</b></Cell>
                  </TableRow>
                </CustomTableHead>
                <TableBody>
                  {usersByRole.map((row, index) => (
                    <TableRow key={index}>
                      <Cell>{row.label}</Cell>
                      <Cell>{row.value}</Cell>
                    </TableRow>
                  ))}
                </TableBody>
              </CustomTable>
            </TableContainer>
          </ContentGraphics>
        </Grid>
      </Grid>

      <Modal open={openModalTrendUsers}>
        <BoxModal>
          <GraphicTrendActiveUsers
            age={age}
            showDates={showDates}
            errorsDate={errorsDate}
            setOpenModalTrendUsers={setOpenModalTrendUsers}
            handleFiltersModal={handleFiltersModal}
          />
        </BoxModal>
      </Modal>
      <BackdropLoading open={open} />
    </Container>
  )
}
